import { createSlice, createListenerMiddleware, PayloadAction } from "@reduxjs/toolkit"

import {
    SurveyAnswer,
    SurveyMultiAnswerValue,
    SurveyQuestion,
    Choices,
    SurveyAnswersStorage,
    filterObject,
    AnswerType,
    PageType,
} from "@modules/NewClientSurvey/common"
import { SEQUENCE_NUMBER } from "@modules/NewClientSurvey/common/constants/sequenceNumber"

const dataStorage = SurveyAnswersStorage.getInstance().get()

export interface SurveyState {
    answers: Record<string, SurveyAnswer>
    question: SurveyQuestion
    currentPage: PageType
}

const initialState: SurveyState = {
    answers: dataStorage?.answers || {},
    question: dataStorage?.currentQuestion || ({} as SurveyQuestion),
    currentPage: dataStorage?.currentPage || PageType.CARDS_PAGE,
}

const surveySlice = createSlice({
    name: "survey",
    initialState,
    reducers: {
        setAnswers(state, action: PayloadAction<Record<string, SurveyAnswer>>) {
            state.answers = action.payload
        },

        initAnswer(
            state,
            action: PayloadAction<{
                questionId: string
                sequence_number: number
                type: AnswerType
                ref: string
            }>
        ) {
            const { questionId, sequence_number, type, ref } = action.payload
            const filteredState = filterObject(state.answers, SEQUENCE_NUMBER, sequence_number)
            state.answers = {
                ...filteredState,
                [questionId]: { value: undefined, type, sequence_number, ref },
            } as Record<string, SurveyAnswer>
        },

        changeInputAnswers(
            state,
            action: PayloadAction<{
                questionId: string
                value: string | number
                sequence_number: number
                type: AnswerType
                ref: string
            }>
        ) {
            const { questionId, value, sequence_number, type, ref } = action.payload
            const filteredState = filterObject(state.answers, SEQUENCE_NUMBER, sequence_number)
            state.answers = {
                ...filteredState,
                [questionId]: { value, type, sequence_number, ref },
            } as Record<string, SurveyAnswer>
        },

        changeMultiSelectAnswers(
            state,
            action: PayloadAction<{
                questionId: string
                choice: Choices
                sequence_number: number
                type: AnswerType
                ref: string
            }>
        ) {
            const { questionId, choice, sequence_number, type, ref } = action.payload
            let choicesValue = {
                ...(state.answers[questionId]?.value as SurveyMultiAnswerValue),
            }
            const choiceValue = choicesValue?.[choice.id]?.value

            if (choiceValue) {
                delete choicesValue?.[choice.id]
            } else {
                choicesValue = {
                    ...choicesValue,
                    [choice.id]: {
                        value: !choicesValue?.[choice.id]?.value,
                        ref: choice.ref,
                    },
                }
            }

            const filteredState = filterObject(state.answers, SEQUENCE_NUMBER, sequence_number)
            state.answers = {
                ...filteredState,
                [questionId]: {
                    value: choicesValue,
                    type,
                    sequence_number,
                    ref,
                },
            } as Record<string, SurveyAnswer>
        },

        setQuestion(state, action: PayloadAction<SurveyQuestion>) {
            state.question = action.payload
        },

        setCurrentPage(state, action: PayloadAction<PageType>) {
            state.currentPage = action.payload
        },
    },
})

export const surveyActions = surveySlice.actions
export const surveyReducer = surveySlice.reducer

export const surveyListenerMiddleware = createListenerMiddleware()
surveyListenerMiddleware.startListening({
    predicate: (action) => typeof action.type === "string" && action.type.startsWith("survey/"),
    effect: (_, listenerApi) => {
        const state = (listenerApi.getState() as { survey: SurveyState }).survey
        const storage = SurveyAnswersStorage.getInstance()
        storage.updateStore("answers", state.answers)
        storage.updateStore("currentQuestion", state.question)
        storage.updateStore("currentPage", state.currentPage)
    },
})
